import { useEffect, useRef, useState } from "react";
import AlertPopup from "../../../../components/common/alertPopup/AlertPopup";
import CustomButton from "../../../../components/common/button/CustomButton";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import ManageInvoice from "../../invoice/ManageInvoice";
import {
  getConfirmationAction,
  getLeadConfirmationLinkAction,
  setLeadConfirmationDownloaded,
  setUpdateConfirmationAction,
} from "../../../../store/reducers/clientConfirmationSlice";
import AlertOnSaveConfirmation from "../../manageLeads/AlertOnSaveConfirmation";
import { setDeletedInvoiceResponse } from "../../../../store/reducers/invoiceSlice";
import ManageDepositInvoice from "../../invoice/ManageDepositInvoice";
import {
  getAllEmailTemplatesAction,
  leadSendEmailTemplateAction,
  setLeadSendEmailTemplateResponseAction,
} from "../../../../store/reducers/emailTemplateSlice";
import SplitTemplateButton from "../../lead/SplitTemplateButton";

const popupTitle: any = {
  email: "Lead Estimate Email",
  pdf: "Lead Estimate PDF",
  odt: "Lead Estimate ODT",
};

const popupTitleQuote: any = {
  email: "Lead Confirmation Email",
  pdf: "Lead Confirmation PDF",
  odt: "Lead Confirmation ODT",
  previous: "Lead Previous Quote",
};

const popupContent: any = {
  email: "Are you sure you want to send an email?",
  pdf: "Are you sure you want to download the PDF?",
  odt: "Are you sure you want to download the ODT?",
  previous: "Are you sure you want to download it?",
};

interface ConfirmationActionsPopupProps {
  confirmationForm: any;
  deleteRec?: string;
  onHide?: () => void;
  setDeleteModalShow?: (show: boolean) => void;
  setShowSuccessMessage?: (message: string) => void;
  clientDetails?: any;
  setIsUpdated?: any;
  screenType?: any;
  setIsShowActionCalled?: any;
  isShowActionCalled?: any;
  setAnyChangeFieldValue?: any;
  anyChangeFieldValue?: any;
  actionType?: any;
  setActionType?: any;
  validateForm?: () => boolean | Promise<boolean>;
}

const ConfirmationActions: React.FC<ConfirmationActionsPopupProps> = ({
  confirmationForm,
  screenType = "",
  isShowActionCalled,
  setIsShowActionCalled,
  setAnyChangeFieldValue,
  anyChangeFieldValue,
  actionType,
  setActionType,
  validateForm,
}) => {
  const [open, setOpen] = useState(false);
  const [invoiceModel, setInvoiceModel] = useState(false);
  const [finalInvoiceModel, setFinalInvoiceModel] = useState(false);
  // Use refs for actionType and selectedTemplate (like QuoteActions)
  const actionTypeRef = useRef("");
  const selectedTemplateRef = useRef<any>(null);
  const { isFileDownloaded, toolTipResponse, isDepositResponse } = useSelector(
    (state: any) => state.confirmation,
  );
  const { isDeletedInvoiceResponse } = useSelector(
    (state: any) => state.invoice,
  );
  const { allEmailTemplates } = useSelector(
    (state: any) => state.emailTemplates,
  );
  const { isSendEmailResponse } = useSelector(
    (state: any) => state.emailTemplates,
  );
  const { userProfile } = useSelector((state: any) => state.user);
  const { leadDetails } = useSelector((state: any) => state.lead);
  const [pendingActionType, setPendingActionType] = useState("");
  const [alertModelShow, setAlertModelShow] = useState(false);
  // Track if we need to send email after GET
  const [shouldSendEmailAfterGet, setShouldSendEmailAfterGet] = useState(false);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAllEmailTemplatesAction({ lead_stage: "CONFIRMED" }));
  }, [dispatch]);
  useEffect(() => {
    if (isFileDownloaded) {
      // submitHandler();
      dispatch(setLeadConfirmationDownloaded(false));
      onHide();
    }
  }, [isFileDownloaded]);

  const onHide = () => {
    setOpen(false);
  };


  // Main action trigger (like QuoteActions)
  const onShow = async (type: string, template?: any) => {
    // For email, validate first
    if (type === "email" && validateForm) {
      const valid = await validateForm();
      if (!valid) return; // If validation fails, do not proceed
    }
    // Store template synchronously in ref before anything async happens
    if (template) {
      selectedTemplateRef.current = template;
    }
    setActionType(type);
    actionTypeRef.current = type;

    // If there are unsaved changes, trigger save first
    if (anyChangeFieldValue) {
      setIsShowActionCalled(true);
    } else {
      // No unsaved changes, fire action immediately
      if (type === "email") {
        sendEmail(template);
      } else {
        submitHandler(type);
      }
    }
  };

  // Send email (like QuoteActions)
  // Only call getConfirmationAction after sendEmail if not in after-save flow
  const sendEmail = (template?: any) => {
    const resolvedTemplate = template || selectedTemplateRef.current;
    if (resolvedTemplate) {
      dispatch(
        leadSendEmailTemplateAction({
          templateId: resolvedTemplate?.template_id || "",
          leadId: leadDetails?.lead?.generated_id || "",
          confirmationId: confirmationForm?.confirmationId || "",
          to: leadDetails?.lead?.email || "",
          template_from: resolvedTemplate?.template_from,
          cc: [],
          bcc: [],
        })
      );
    }
    selectedTemplateRef.current = null;
    actionTypeRef.current = "";
    setShouldSendEmailAfterGet(false);
  };

  useEffect(() => {
    if (isSendEmailResponse) {
      dispatch(getConfirmationAction(leadDetails?.lead?.generated_id));
      dispatch(setLeadSendEmailTemplateResponseAction(false));
    }
  }, [dispatch, isSendEmailResponse, leadDetails?.lead?.generated_id]);

  // Standard submit for PDF/ODT
  const submitHandler = (typeParam?: string) => {
    const finalType = typeParam || actionTypeRef.current || actionType;
    dispatch(
      getLeadConfirmationLinkAction({
        leadId: leadDetails?.lead?.generated_id,
        ...confirmationForm,
        type: finalType,
        screenType,
      })
    );
    setIsShowActionCalled(false);
    setActionType("");
    setAnyChangeFieldValue(false);
    actionTypeRef.current = "";
    selectedTemplateRef.current = null;
  };

  const handleClose = () => {
    setInvoiceModel(false);
    setFinalInvoiceModel(false);
    dispatch(getConfirmationAction(leadDetails?.lead?.generated_id));
  };
  const handleInvoice = () => {
    setInvoiceModel(true);
  };


  // Match QuoteActions: On save success, immediately call sendEmail if needed
  const {confirmationResponse} = useSelector((state: any) => state.confirmation);
  useEffect(() => {
    if (confirmationResponse && actionTypeRef.current) {
      const currentType = actionTypeRef.current;
      const currentTemplate = selectedTemplateRef.current;
      if (currentType === "email") {
        sendEmail(currentTemplate);
      } else {
        submitHandler(currentType);
      }
      setIsShowActionCalled(false);
      setAnyChangeFieldValue(false);
      setActionType("");
      actionTypeRef.current = "";
      selectedTemplateRef.current = null;
      dispatch(setUpdateConfirmationAction(null));
    }
    // eslint-disable-next-line
  }, [confirmationResponse]);

  const isCheckedQuoteStatus = leadDetails?.lead?.status === "QUOTE";

  // 🆕 main click handler
  const handleActionClick = (type: string) => {
    if (isCheckedQuoteStatus) {
      // open modal first
      setPendingActionType(type);
      setAlertModelShow(true);
    } else {
      // directly call existing logic
      onShow(type);
    }
  };

  // 🆕 called when modal submit is clicked
  const handleModalSubmit = () => {
    setAlertModelShow(false);
    if (pendingActionType) {
      onShow(pendingActionType);
      setPendingActionType("");
    }
  };

  useEffect(() => {
    if (isDeletedInvoiceResponse) {
      setInvoiceModel(false);
      dispatch(setDeletedInvoiceResponse(false));
    }
  }, [isDeletedInvoiceResponse]);

  const handleSendEmailWithTemplate = (template: any) => {
    dispatch(
      leadSendEmailTemplateAction({
        templateId: template?.template_id || "",
        leadId: leadDetails?.lead?.generated_id || "",
        confirmationId: confirmationForm?.confirmationId || "",
        to: leadDetails?.lead?.email || "",
        template_from: template?.template_from,
        cc: [],
        bcc: [],
      }),
    );
  };

  const handleFinalInvoice = () => {
    setFinalInvoiceModel(true);
  };


  return (
    <>
      {alertModelShow && (
        <AlertOnSaveConfirmation
          open={alertModelShow}
          title="Confirmation"
          onHide={() => setAlertModelShow(false)}
          setAlertModelShow={setAlertModelShow}
          submitHandler={handleModalSubmit} // 🆕 calls onShow(type)
        />
      )}
      {invoiceModel && (
        <ManageDepositInvoice
          title={"Deposit Invoice"}
          open={invoiceModel}
          onHide={handleClose}
          invoiceType="deposit"
          isSetLeadDetails={false}
          isNewInvoice={false}
          selectedInvoice={null}
          selectedQuote={confirmationForm}
        />
      )}
      {finalInvoiceModel && (
        <ManageInvoice
          title="Invoice"
          open={finalInvoiceModel}
          onHide={handleClose}
          invoiceType="final"
          isFormReadyOnly={false}
          isSetLeadDetails={false}
          isNewInvoice={false}
          isCompletionInvoice={false}
          selectedQuote={confirmationForm}
        />
      )}
      <CustomButton
        // disabled={
        //   screenType === "confirmation"
        //     ? confirmationForm?.invoiceNumber
        //       ? false
        //       : true
        //     : true
        // }
        disabled={leadDetails?.lead?.status === "STORAGE"}
        style={{ marginRight: "10px" }}
        title="Deposit"
        onClick={() => handleInvoice()}
      />
      {/* {userProfile?.features?.includes("Professional Quotes") ? ( */}
        <SplitTemplateButton
          title="Email"
          options={allEmailTemplates}
          disabled={!confirmationForm?.isDepositeRecieved || leadDetails?.lead?.status === "STORAGE"}
          onButtonClick={async () => {
            let defaultTemplate = allEmailTemplates.find((tpl: any) => tpl.isDefault || tpl.is_primary);
            if (!defaultTemplate && allEmailTemplates && allEmailTemplates.length > 0) {
              defaultTemplate = allEmailTemplates[0];
            }
            if (defaultTemplate) {
              await onShow("email", defaultTemplate);
            }
          }}
          onSelect={async (template: any) => {
            await onShow("email", template);
          }}
        />
      {/* ) : (
        <CustomButton
          disabled={!confirmationForm?.isDepositeRecieved}
          style={{ marginRight: "10px" }}
          title="Email"
          onClick={() => handleActionClick("email")}
        />
      )} */}
      <CustomButton
        disabled={!confirmationForm?.isDepositeRecieved || leadDetails?.lead?.status === "STORAGE"}
        // disabled={
        //   screenType === "confirmation" &&  confirmationForm?.confirmationId ? false: true
        // }
        style={{ marginRight: "10px" }}
        title="PDF"
        // onClick={() => onShow("pdf")}
        onClick={() => handleActionClick("pdf")}
      />
      {/* Todo */}

      <CustomButton
        disabled={leadDetails?.lead?.status !== "STORAGE" &&
          screenType === "confirmation" &&
          confirmationForm?.confirmationId &&
          confirmationForm?.isDepositeRecieved 
            ? false
            : true
        }
        style={{ marginRight: "10px" }}
        title="ODT"
        // onClick={() => onShow("odt")}
        onClick={() => handleActionClick("odt")}
      />

      <CustomButton
        disabled={
         ( leadDetails?.lead?.status === "JOB" ||
          leadDetails?.lead?.status === "COMPLETED" || leadDetails?.lead?.status !== "STORAGE" ||
          leadDetails?.lead?.status === "DISPUTE") && confirmationForm?.isQuoteAccepted
            ? false
            : true
        }
        style={{ marginRight: "10px" }}
        title="Final Invoice"
        // onClick={() => onShow("odt")}
        onClick={() => handleFinalInvoice()}
      />

      {/* {screenType === "confirmation" && <CustomButton
                disabled={
                    screenType === "confirmation" ?
                    (confirmationForm?.quoteId && confirmationForm?.count > 1) ? false : true :
                    confirmationForm?.estimateId ? false : true
                }
                style={{ marginRight: "20px", marginTop: "5px", float: "right" }} title="Previous Quote" onClick={()=>onShow('previous')}
            />} */}

      <AlertPopup
        open={open}
        onHide={onHide}
        title={
          screenType === "confirmation"
            ? popupTitleQuote[actionType]
            : popupTitle[actionType]
        }
        content={popupContent[actionType]}
        submitHandler={submitHandler}
      />
    </>
  );
};

export default ConfirmationActions;
